import {
  handleSubmitMultilingualForm,
  useCreateSelectionPage,
  useGetSelectionFormData,
  useUpdateSelection,
} from "@/api/cms.ts";
import { useGetGlobalMode, useGetSections } from "@/api/section";
import { useGetLayout } from "@/api/selection";
import GlobalSelection from "@/components/cms/selection/GlobalSelection";
import { GlobalSelectionToggle } from "@/components/cms/selection/GlobalSelectionToggle";
import { GridDisplay } from "@/components/cms/selection/GridDisplay";
import { SelectionForm } from "@/components/cms/selection/SelectionForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button.tsx";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema, selectionContentSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  selectionContentSchema
    .omit({ id: true })
    .merge(cmsPageSchema.pick({ pageName: true, published: true }))
    .optional(),
);

export type FormData = z.infer<typeof formSchema>;

const SelectionCMS = () => {
  const { instanceData } = useAuth();
  const { t } = useTranslation();

  const { data: selectionPageData } = useGetSelectionFormData();
  const { data: sections } = useGetSections();
  const { data: layout } = useGetLayout();
  const { data: globalModeData } = useGetGlobalMode();

  const create = useCreateSelectionPage();
  const update = useUpdateSelection();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const selection = selectionPageData?.[lang];
        return {
          published: selection?.published ?? false,
          pageName: selection?.pageName ?? "",
          content: selection?.content.content,
        };
      }),
    [selectionPageData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    if (selectionPageData) {
      form.reset(defaultValues);
    }
  }, [selectionPageData, defaultValues, form]);

  const globalSectionId = useMemo(() => {
    return sections?.find((section) => section.isGlobalSelection)?.id || null;
  }, [sections]);

  const isGlobalMode = useMemo(() => {
    return globalModeData?.isGlobalMode || false;
  }, [globalModeData]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = selectionPageData?.[key as SupportedLocale];

      if (!localeData) return;

      const { pageName, published, content } = localeData;

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content: { content },
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("cms.selection.success"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("cms.selection.error"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout
      formIds={["selection"]}
      pageName={
        selectionPageData?.[getMainLanguage()]?.pageName ?? t("cmsTabSelection")
      }
    >
      <Form {...form}>
        <form
          id="selection"
          onSubmit={handleSubmitMultilingualForm(
            form,
            selectionPageData,
            onSubmit,
          )}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={true} name={`${lang}.published`} />
              )}
              render={(lang) => <SelectionForm lang={lang} />}
            />
          </div>
          <GlobalSelectionToggle isGlobalMode={isGlobalMode} />

          {!isGlobalMode && (
            <>
              <div className=" w-full flex justify-between items-center">
                <p className="text-[#0c111d] text-base font-medium font-['Raleway'] leading-normal">
                  {t("cms.selection.sections.title")}
                </p>
                <div className="flex gap-3 items-center">
                  <Link to="manage-layout">
                    <Button variant={"outline"}>
                      {t("cms.selection.sections.manageLayout.button")}
                    </Button>
                  </Link>
                  <Link to="edit-section">
                    <Button variant={"outline"}>
                      {t("cms.selection.sections.addSection.button")}
                    </Button>
                  </Link>
                </div>
              </div>
              {sections && layout && (
                <GridDisplay
                  displayable={{ dType: "section", data: sections }}
                  layout={layout}
                />
              )}
            </>
          )}

          {isGlobalMode && globalSectionId && (
            <GlobalSelection globalSectionId={globalSectionId} />
          )}
        </form>
      </Form>
    </CMSLayout>
  );
};
export default SelectionCMS;
